import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_left, QIcon_chevron_right } from '@repo/icons/index'
import format from 'date-fns/format'
import { enUS, ko } from 'date-fns/locale'
import { useEffect, useMemo, useState } from 'react'
import useIsMobile from '../../../../../modules/hooks/common/useIsMobile'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import ChallengeStamp from './ChallengeStamp'

const CHALLENGE = [
  {
    DURATION: 40,
    TOTAL_STAMP: 24,
    POINT_INTERVAL: 6,
    POINT_REWARD: 5000,
  },
  { DURATION: 20, TOTAL_STAMP: 48, POINT_INTERVAL: 12, POINT_REWARD: 5000 },
] as const

interface ChallengeStampBoardProps {
  duration?: 40 | 20
  completedDates?: string[]
}

const getTextColor = (isStamped: boolean, reward: number | null) => {
  if (isStamped && reward) return 'text-red-400'
  if (isStamped && !reward) return 'text-purple-400'
  return 'text-gray-500'
}

const ChallengeStampBoard = ({ duration = 40, completedDates = [] }: ChallengeStampBoardProps) => {
  const [currentBoard, setCurrentBoard] = useState(1)

  const { t, isLocaleKO } = useTranslation()
  const isMobile = useIsMobile()

  const { TOTAL_STAMP, POINT_INTERVAL, POINT_REWARD } = CHALLENGE.find((challenge) => challenge.DURATION === duration)
  const stampsPerPage = 24

  const stampData = useMemo(
    () => generateStampData(TOTAL_STAMP, completedDates, POINT_INTERVAL, POINT_REWARD, isLocaleKO),
    [TOTAL_STAMP, completedDates, POINT_INTERVAL, POINT_REWARD]
  )

  const paginatedNumbers = stampData.slice((currentBoard - 1) * stampsPerPage, currentBoard * stampsPerPage)

  useEffect(() => {
    setCurrentBoard(1)
  }, [duration])

  return (
    <div className="p-20 sm:py-40 sm:px-48 w-full sm:w-[830px] flex flex-col gap-y-[60px] bg-white border rounded-lg">
      <section className="sm:min-w-[732px] grid grid-cols-4 sm:grid-cols-8 gap-y-16 sm:gap-y-32 justify-items-center">
        {paginatedNumbers.map(({ label, isStamped, point, displayLabel }, i) => {
          const textColor = getTextColor(isStamped, point)
          const isLast = currentBoard === Math.ceil(TOTAL_STAMP / stampsPerPage) && i === paginatedNumbers.length - 1
          const stampSize = { outerHeight: isMobile ? 48 : 60, innerHeight: isMobile ? 38 : 48 }
          return (
            <div key={label} className="flex flex-col gap-y-8 items-center">
              <ChallengeStamp
                label={label}
                reward={point}
                isStamped={isStamped}
                outerHeight={stampSize.outerHeight}
                innerHeight={stampSize.innerHeight}
              />
              <div className="flex flex-col text-center">
                {isLast && (
                  <span className={`Caption-12Md sm:Body2-14Md whitespace-pre-line ${textColor}`}>
                    {t([`실리콘밸리\n투어 응모권`, `Chance to win Silicon Valley Tour`])}
                  </span>
                )}
                <span className={`Body2-14Md sm:Body1-16Md whitespace-nowrap ${textColor}`}>{displayLabel}</span>
              </div>
            </div>
          )
        })}
      </section>

      {TOTAL_STAMP === 48 && (
        <div className="flex gap-x-4 items-center justify-center">
          <button disabled={currentBoard === 1} onClick={() => setCurrentBoard(1)}>
            <QIcon_chevron_left width={16} height={16} color={currentBoard === 1 ? COLORS.GRAY300 : COLORS.BLACK} />
          </button>
          <div className="flex gap-x-4">
            <span className="Body2-14Md">{currentBoard}</span>
            <span className="Body2-14Md text-gray-300">/</span>
            <span className="Body2-14Md text-gray-300">{TOTAL_STAMP / stampsPerPage}</span>
          </div>
          <button disabled={currentBoard === 2} onClick={() => setCurrentBoard(2)}>
            <QIcon_chevron_right width={16} height={16} color={currentBoard === 2 ? COLORS.GRAY300 : COLORS.BLACK} />
          </button>
        </div>
      )}
    </div>
  )
}

export default ChallengeStampBoard

export const generateStampData = (
  totalStamp: number,
  completedDates: any,
  pointInterval: number,
  pointReward: number,
  isLocaleKO: boolean
) => {
  return Array.from({ length: totalStamp }, (_, index) => {
    const label = index + 1
    const isStamped = label <= completedDates.length
    const isPointInterval = label % pointInterval === 0
    const pointValue = isPointInterval ? Math.floor(label / pointInterval) * pointReward : null

    const displayLabel = isStamped
      ? isLocaleKO
        ? format(new Date(completedDates[index]?.start_time), 'M월 d일', { locale: ko })
        : format(new Date(completedDates[index]?.start_time), 'MMM d', { locale: enUS })
      : pointValue
      ? `+${pointValue.toLocaleString()}P`
      : '-'

    return { label: index + 1, isStamped, point: isStamped ? pointValue : null, displayLabel }
  })
}
